import { atom } from "jotai";
import {
    AccountHintType,
    DraftAccountMeta,
} from "@/entities/DataAssociation/Draft.model";
import {
    Account,
    AccountLabel,
    BalanceSheetCategory,
    IncomeStatementCategory,
} from "@/entities/DataAssociation/Account.model";
import { QueryAtom } from "@/api/types/query.types";
import { ACCOUNT_LIST } from "../labeling.constants";
import { selectedRowAtom } from "./table.state";
import { derivedStatementTypesAtom } from "./draft.state";
import { StatementType } from "@/entities/DataAssociation/File.model";

export type NormalizedAccount = {
    id: string;
    name: string;
    label: AccountLabel;
    type: AccountHintType;
    meta?: DraftAccountMeta | null;
};

export const existingAccountsAtom = atom<QueryAtom<Account[]>>({
    isLoading: true,
    isError: false,
    data: [],
});

export const existingAccountsEditsAtom = atom<
    Record<string, Partial<NormalizedAccount>>
>({});

export const newAccountsAtom = atom<Record<string, NormalizedAccount>>({});

export const accountsAtom = atom(
    (get) => {
        const accountResponse = get(existingAccountsAtom);
        const accountEdits = get(existingAccountsEditsAtom);
        const newAccounts = Object.values(get(newAccountsAtom));

        const accounts = (accountResponse.data || []).reduce(
            (acc, account) => {
                acc[account.id] = {
                    id: account.id,
                    name: account.accountName,
                    label: account.label,
                    type: AccountHintType.EXISTING,
                };
                return acc;
            },
            {} as Record<string, NormalizedAccount>,
        );

        Object.values(accounts).forEach((account) => {
            accounts[account.id] = {
                ...accounts[account.id],
                ...accountEdits[account.id],
            };
        });

        newAccounts.forEach((account) => {
            accounts[account.id] = account;
        });

        return accounts;
    },
    (get, set, value: NormalizedAccount) => {
        if (value.type === AccountHintType.EXISTING) {
            set(existingAccountsEditsAtom, (prev) => ({
                ...prev,
                [value.id]: value,
            }));
        } else if (value.type === AccountHintType.NEW) {
            set(newAccountsAtom, (prev) => ({
                ...prev,
                [value.id]: value,
            }));
        }
    },
);

// Pure Function to get the display list of accounts
export const accountDisplayListAtom = atom((get) => {
    const accounts = get(accountsAtom);
    const selectedRow = get(selectedRowAtom);
    const derivedStatementTypes = get(derivedStatementTypesAtom);
    const fn = function (searchTerm: string, showSelectedRow: boolean = false) {
        const filteredAccounts = Object.values(accounts).filter((account) => {
            const metadata = account.label ? ACCOUNT_LIST[account.label] : null;
            const term = searchTerm.toLowerCase();
            if (
                selectedRow?.account?.accountId &&
                selectedRow.account.accountId === account.id &&
                !showSelectedRow
            ) {
                return false;
            }
            return (
                (metadata?.category?.toLowerCase().includes(term) ?? false) ||
                (account.name?.toLowerCase().includes(term) ?? false) ||
                (account.label?.toLowerCase().includes(term) ?? false) ||
                (metadata?.code?.toLowerCase().includes(term) ?? false) ||
                (metadata?.statementType?.toLowerCase().includes(term) ?? false)
            );
        });

        return filteredAccounts.sort((a, b) => {
            const metadataA = ACCOUNT_LIST[a.label];
            const metadataB = ACCOUNT_LIST[b.label];

            if (!metadataA || !metadataB) {
                if (!metadataA && !metadataB) return 0;
                return metadataA ? -1 : 1;
            }

            if (selectedRow?.account) {
                const firstCategory = selectedRow.account.label
                    ? ACCOUNT_LIST[selectedRow.account.label]?.category
                    : derivedStatementTypes.includes(
                            StatementType.INCOME_STATEMENT,
                        )
                      ? IncomeStatementCategory.REVENUES
                      : BalanceSheetCategory.ASSETS;

                const aMatchesFirst = metadataA.category === firstCategory;
                const bMatchesFirst = metadataB.category === firstCategory;
                if (aMatchesFirst && !bMatchesFirst) return -1;
                if (bMatchesFirst && !aMatchesFirst) return 1;
            }

            return (
                (metadataA.category ?? "").localeCompare(
                    metadataB.category ?? "",
                ) ||
                (metadataA.code ?? "").localeCompare(metadataB.code ?? "") ||
                (metadataA.name ?? "").localeCompare(metadataB.name ?? "")
            );
        });
    };
    return fn;
});
